import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import axiosInstance from 'apis/axiosConfig';

const EmiWalletList = () => {
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentLoan, setCurrentLoan] = useState(null);
    const [customAmount, setCustomAmount] = useState('');

    const fetchData = async () => {
        try {
            const res = await axiosInstance.get('loans-data');
            // Show only active loans where EMI needs to be collected
            const activeLoans = res.data.filter(loan => loan.loan_status === 'active');
            setData(activeLoans);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleActionClick = (row) => {
        setCurrentLoan(row);
        setCustomAmount(row.emi_frequency === 'Flexible' ? '' : row.monthly_emi);
        setShowModal(true);
    };

    const handleMarkPaid = async () => {
        const isFlexible = currentLoan.emi_frequency === 'Flexible';
        if(!isFlexible && currentLoan.remaining_emi_count <= 0) {
            toast.error("All EMIs are already paid for this loan!");
            return;
        }

        let collectionAmount = parseFloat(customAmount);
        if (isNaN(collectionAmount) || collectionAmount <= 0) {
            toast.error("Please enter a valid amount!");
            return;
        }
        
        let newPaidCount = parseInt(currentLoan.emi_paid_count) + 1;
        let newRemAmount = parseFloat(currentLoan.remaining_amount) - collectionAmount;
        if (newRemAmount < 0) newRemAmount = 0;

        let newRemCount = parseInt(currentLoan.remaining_emi_count);
        if (!isFlexible) {
            newRemCount = newRemCount - 1;
        } else if (newRemAmount <= 0) {
            newRemCount = 0; // flexible loan fully paid
        }

        // Auto complete loan if 0 remaining
        const newStatus = (newRemCount <= 0 || newRemAmount <= 0) ? 'completed' : currentLoan.loan_status;

        try {
            await axiosInstance.put(`loans-data/${currentLoan.id}`, {
                emi_paid_count: newPaidCount,
                remaining_emi_count: newRemCount,
                remaining_amount: newRemAmount,
                loan_status: newStatus
            });
            toast.success(`₹${collectionAmount} Collected Successfully!`);
            setShowModal(false);
            fetchData();
        } catch (err) {
            toast.error("Failed to collect Payment");
        }
    };

    const getLoanFrequencyData = (row) => {
        const paidCount = parseInt(row.emi_paid_count || 0);
        const remCount = parseInt(row.remaining_emi_count || 0);
        const rawTenure = parseInt(row.loan_tenure_months || 12);

        let totalTenure = row.total_tenure_count || (paidCount + remCount) || rawTenure;
        let emiLabel = row.emi_frequency ? `${row.emi_frequency} EMI` : "Monthly EMI";

        return { emiLabel, totalTenure };
    };

    const tableHeaders = [
        { Header: 'Loan ID', accessor: 'id' },
        { Header: 'User ID', accessor: 'loan_user_id' },
        { 
            Header: 'EMI Amount', 
            accessor: 'monthly_emi', 
            Cell: ({ row }) => {
                const { emiLabel } = getLoanFrequencyData(row.original);
                return (
                    <div className="d-flex flex-column">
                        <small className="text-muted" style={{fontSize: '10px'}}>{emiLabel}</small>
                        <strong className="text-primary">₹{row.original.monthly_emi}</strong>
                    </div>
                )
            }
        },
        { Header: 'Paid EMIs', accessor: 'emi_paid_count', Cell: ({ row }) => <span className="text-success font-weight-bold">{row.original.emi_paid_count}</span> },
        { 
            Header: 'EMI Progress', 
            accessor: 'remaining_emi_count', 
            Cell: ({ row }) => {
                const { totalTenure } = getLoanFrequencyData(row.original);
                return <span className="text-primary font-weight-bold">{row.original.emi_paid_count}/{totalTenure}</span>
            }
        },
        { Header: 'Rem. Amount', accessor: 'remaining_amount', Cell: ({ row }) => `₹${row.original.remaining_amount}` },
        { Header: 'Action', accessor: 'action', Cell: ({ row }) => (<button className="btn btn-success btn-sm font-weight-bold" onClick={() => handleActionClick(row.original)}><i className="fa fa-inr"></i> {row.original.emi_frequency === 'Flexible' ? 'Collect Pay' : 'Collect EMI'}</button>) }
    ];

    return (
        <div className='container-fluid mt-5'>
            <div className='mainheadig mx-4 d-flex'>
                <h4 className='text-white font-weight-bold'>EMI & Wallet Collections</h4>
            </div>
            {data?.length > 0 ? (
                <AdminTable tableHeaders={tableHeaders} tableData={data} />
            ) : <p className='container-fluid bg-info w-100 p-2 mx-4 text-center text-white'>No Active Loans available for EMI Collection</p>}

            {/* Collect EMI Modal */}
            {showModal && currentLoan && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title font-weight-bold">Collect EMI Payment</h5>
                                <button type="button" className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body row">
                                <div className="col-12 mb-3 text-center">
                                    {currentLoan.emi_frequency === 'Flexible' ? (
                                        <>
                                            <h5 className="text-primary font-weight-bold mb-2">Open Khata Repayment</h5>
                                            <div className="input-group input-group-lg mx-auto" style={{maxWidth: '250px'}}>
                                                <div className="input-group-prepend"><span className="input-group-text">₹</span></div>
                                                <input type="number" className="form-control font-weight-bold" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="Amount" autoFocus />
                                            </div>
                                            <p className="text-muted mt-2">Enter custom amount to collect from user</p>
                                        </>
                                    ) : (
                                        <>
                                            <h1 className="text-primary font-weight-bold">₹{currentLoan.monthly_emi}</h1>
                                            <p className="text-muted">EMI Amount to be Collected</p>
                                        </>
                                    )}
                                </div>
                                <div className="col-6 mb-2"><strong>Total Paid EMIs:</strong> {currentLoan.emi_paid_count}</div>
                                {currentLoan.emi_frequency !== 'Flexible' && (
                                    <div className="col-6 mb-2 text-danger"><strong>Remaining EMIs:</strong> {currentLoan.remaining_emi_count}/{getLoanFrequencyData(currentLoan).totalTenure}</div>
                                )}
                                <div className="col-12 mt-3">
                                    <div className="alert alert-warning border-warning">
                                        <strong>Confirm Action:</strong> Clicking &quot;Confirm &amp; Mark as Paid&quot; will register this payment and reduce the remaining amount. This action is irreversible.
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-success font-weight-bold" onClick={handleMarkPaid}>Confirm & Mark as Paid</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
export default EmiWalletList;
